import { cva } from 'class-variance-authority'
import { cn } from '@/lib/utils'
import Image from 'next/image'

import { Headline } from '@/components/ui/headline'
import { Badge } from '@/components/ui/badge'
import Link from "next/link";
import {Media, Post} from '@/root/payload-types'
import {useLocale} from "@/hooks/useLocale";
import PlaceholderImg from "@/images/dummy/placeholder-product.png";
import {Separator} from "@/components/ui/separator";
import React, { useMemo, useState } from "react";
import { useTranslations } from "@/contexts/TranslationContext";

const articleCardVariants = cva(
    'card relative flex flex-col overflow-clip rounded-md transition-transform duration-300 hover:-translate-y-2',
    {
        variants: {
            size: {
                small: 'sm:col-span-1',
                large: 'col-span-full'
            },
            type: {
                default: 'ul',
                ul: 'ol',
                ol: 'ul'
            }
        },
        defaultVariants: {
            size: 'small'
        }
    }
)

interface ArticleCardProps {
    size?: "small" | "large" | null | undefined;
    bgColor?: string;
    category?: string;
    url: string;
    post: Post;
    useAbsolutePath?: boolean;
    view?: "list" | "card";
}

/**
 * @param {string} id Component ID.
 * @param {string} size Optional, component size. Default = small. Options = small, large.
 * @param {string} bgColor Card background color.
 * @param {string} category Article category.
 * @param {string} date Article publish date.
 * @param image Image data.
 * @param {string} title Article heading.
 * @param {string} content Article content.
 * @param {string} url Article link.
 */
const ArticleCard = ({ size = 'small', bgColor = "primary", category, url, post, useAbsolutePath = false, view = "card" }: ArticleCardProps) => {
    const locale = useLocale();
    const [ language ] = locale.split('-');

    const [imageError, setImageError] = useState(false);

    const t = useTranslations();

    let content = post.content?.find(content => content.language === language);
    if(!content) content = post.content?.find(content => content.language === "en");

    const isNew = useMemo(() => {
      const postDate = new Date(post.date ?? post.createdAt);
      const now = new Date();
      const diffTime = Math.abs(now.getTime() - postDate.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays <= 5;
    }, [post.date, post.createdAt]);

  return (
        view === "card" ? (
            <div className={cn(articleCardVariants({ size }), `border-2`)} data-article-id={post.id}>
                <Link className={`${size === 'large' ? 'flex flex-col md:flex-row' : ''}`} href={useAbsolutePath ? `/${url}` : url}>
                    <div className={`images relative aspect-video ${size === 'large' ? 'md:w-1/2 lg:w-2/3' : ''}`}>
                        {isNew && (
                          <div className="absolute top-2 right-2 z-10">
                            <Badge variant="aloevera">{t["articleCard.new"]}</Badge>
                          </div>
                        )}
                      <Image
                            className='w-full object-scale-down'
                            fill
                            src={(post?.featuredImage as Media)?.url ?? PlaceholderImg.src}
                            alt={(post?.featuredImage as Media)?.alt ?? ""}
                            onError={event => console.warn("debug", "error fetching image")}
                            onLoad={() => console.log("debug", "load complete")}
                            priority={true}
                        />
                    </div>
                    <div className={`bg-${bgColor} text-${bgColor}-foreground content p-4 lg:p-8 h-full ${size === 'large' ? 'flex flex-col justify-center md:w-1/2 lg:w-1/3' : ''}`}>
                        <div className='head'>
                            <div className='flex flex-row justify-between items-center'>
                                {<Badge variant={'outline-invert'}>{new Date(post.date ?? post.createdAt).toLocaleDateString()}</Badge>}
                                {<Badge variant={'outline-invert'}>{post.user}</Badge>}
                            </div>
                            <Headline variant='h4' size='lg' className='mt-2 font-normal'>
                                {content?.title}
                            </Headline>
                            <div className={"text-sm"}>
                                {content?.subtitle}
                            </div>
                        </div>
                    </div>
                </Link>
            </div>
        ) :
            <Link href={useAbsolutePath ? `/${url}` : url} className={"border-2 rounded-md"}>
                <div className={"hover:bg-gray-beige-light hover:cursor-pointer rounded-md"}>
                    <div className={"flex items-center"}>
                        <Image
                            src={imageError ? PlaceholderImg.src : String((post?.featuredImage as Media)?.url)}
                            alt={"Image"}
                            width={128}
                            height={128}
                            className="rounded-l-md"
                            onError={() => setImageError(true)}
                            priority={true}
                        />
                        <div>
                            <p className={"px-2 font-bold"}>{content?.title}</p>
                            <p className={"px-2 text-sm"}>{content?.subtitle}</p>
                            <p className={"text-xs px-2 pt-2"}>{post.user} - {new Date(post.date ?? post.createdAt).toLocaleDateString()}</p>
                        </div>
                    </div>
                </div>
            </Link>
    )
}

export { ArticleCard }
